import { Routes } from '@angular/router';

import {
  authGuard,
  chooseUsernameGuard,
  guestGuard,
  usernameRequiredGuard,
} from './core/guards/auth.guard';
import {
  comingSoonAuthGuard,
  constructionPageGuard,
  siteStatusChildGuard,
  siteStatusGuard,
} from './core/guards/site-status.guard';

export const routes: Routes = [
  {
    path: 'coming-soon',
    canActivate: [siteStatusGuard],
    loadComponent: () =>
      import('./screens/coming-soon-screen/coming-soon-screen.component').then(
        (m) => m.ComingSoonScreenComponent,
      ),
  },
  {
    path: 'under-construction',
    canActivate: [constructionPageGuard],
    loadComponent: () =>
      import('./screens/under-construction-screen/under-construction-screen.component').then(
        (m) => m.UnderConstructionScreenComponent,
      ),
  },
  {
    path: '',
    canActivate: [siteStatusGuard],
    canActivateChild: [siteStatusChildGuard],
    children: [
      {
        path: '',
        pathMatch: 'full',
        canActivate: [guestGuard],
        loadComponent: () =>
          import('./screens/landing-screen/landing-screen.component').then(
            (m) => m.LandingScreenComponent,
          ),
      },
      {
        path: 'signin',
        canActivate: [comingSoonAuthGuard, guestGuard],
        loadComponent: () =>
          import('./screens/sign-in-screen/sign-in-screen.component').then(
            (m) => m.SignInScreenComponent,
          ),
      },
      {
        path: 'signup',
        canActivate: [comingSoonAuthGuard, guestGuard],
        loadComponent: () =>
          import('./screens/sign-up-screen/sign-up-screen.component').then(
            (m) => m.SignUpScreenComponent,
          ),
      },
      {
        path: 'cookie',
        data: { legalPage: 'cookies' },
        loadComponent: () =>
          import('./screens/legal-screen/legal-screen.component').then(
            (m) => m.LegalScreenComponent,
          ),
      },
      {
        path: 'cookies',
        redirectTo: 'cookie',
        pathMatch: 'full',
      },
      {
        path: 'privacy',
        data: { legalPage: 'privacy' },
        loadComponent: () =>
          import('./screens/legal-screen/legal-screen.component').then(
            (m) => m.LegalScreenComponent,
          ),
      },
      {
        path: 'terms',
        data: { legalPage: 'terms' },
        loadComponent: () =>
          import('./screens/legal-screen/legal-screen.component').then(
            (m) => m.LegalScreenComponent,
          ),
      },
      {
        path: 'choose-username',
        canActivate: [authGuard, chooseUsernameGuard],
        loadComponent: () =>
          import('./screens/choose-username-screen/choose-username-screen.component').then(
            (m) => m.ChooseUsernameScreenComponent,
          ),
      },
      {
        path: '',
        canActivate: [authGuard, usernameRequiredGuard],
        loadComponent: () =>
          import('./elements/layout/app-shell.component').then(
            (m) => m.AppShellComponent,
          ),
        children: [
          {
            path: 'overview',
            redirectTo: 'my-data',
            pathMatch: 'full',
          },
          {
            path: 'my-data',
            loadComponent: () =>
              import('./elements/dashboard/dashboard-shell.component').then(
                (m) => m.DashboardShellComponent,
              ),
            children: [
              {
                path: '',
                pathMatch: 'full',
                redirectTo: 'overview',
              },
              {
                path: 'overview',
                loadComponent: () =>
                  import('./elements/dashboard/dashboard-overview-tab.component').then(
                    (m) => m.DashboardOverviewTabComponent,
                  ),
              },
              {
                path: 'podiums',
                loadComponent: () =>
                  import('./elements/dashboard/dashboard-podiums-tab.component').then(
                    (m) => m.DashboardPodiumsTabComponent,
                  ),
              },
              {
                path: 'sessions',
                loadComponent: () =>
                  import('./elements/dashboard/dashboard-sessions-tab.component').then(
                    (m) => m.DashboardSessionsTabComponent,
                  ),
              },
              {
                path: 'follows',
                loadComponent: () =>
                  import('./elements/social/account-network.component').then(
                    (m) => m.AccountNetworkComponent,
                  ),
              },
            ],
          },
          {
            path: 'compare',
            loadComponent: () =>
              import('./screens/compare-screen/compare-screen.component').then(
                (m) => m.CompareScreenComponent,
              ),
          },
          {
            path: 'compare/new',
            loadComponent: () =>
              import('./screens/compare-builder-screen/compare-builder-screen.component').then(
                (m) => m.CompareBuilderScreenComponent,
              ),
          },
          {
            path: 'compare/:comparisonId',
            loadComponent: () =>
              import('./screens/compare-detail-screen/compare-detail-screen.component').then(
                (m) => m.CompareDetailScreenComponent,
              ),
          },
          {
            path: 'drivers',
            loadComponent: () =>
              import('./screens/drivers-screen/drivers-screen.component').then(
                (m) => m.DriversScreenComponent,
              ),
          },
          {
            path: 'drivers/:driverId',
            loadComponent: () =>
              import('./elements/dashboard/dashboard-shell.component').then(
                (m) => m.DashboardShellComponent,
              ),
            children: [
              {
                path: '',
                pathMatch: 'full',
                redirectTo: 'overview',
              },
              {
                path: 'overview',
                loadComponent: () =>
                  import('./elements/dashboard/dashboard-overview-tab.component').then(
                    (m) => m.DashboardOverviewTabComponent,
                  ),
              },
              {
                path: 'podiums',
                loadComponent: () =>
                  import('./elements/dashboard/dashboard-podiums-tab.component').then(
                    (m) => m.DashboardPodiumsTabComponent,
                  ),
              },
              {
                path: 'sessions',
                loadComponent: () =>
                  import('./elements/dashboard/dashboard-sessions-tab.component').then(
                    (m) => m.DashboardSessionsTabComponent,
                  ),
              },
            ],
          },
          {
            path: 'sessions/new',
            loadComponent: () =>
              import('./screens/session-entry-screen/session-entry-screen.component').then(
                (m) => m.SessionEntryScreenComponent,
              ),
          },
          {
            path: 'sessions/:sessionId/edit',
            loadComponent: () =>
              import('./screens/session-entry-screen/session-entry-screen.component').then(
                (m) => m.SessionEntryScreenComponent,
              ),
          },
          {
            path: 'tracks',
            loadComponent: () =>
              import('./screens/tracks-screen/tracks-screen.component').then(
                (m) => m.TracksScreenComponent,
              ),
          },
          {
            path: 'tracks/:trackId/stats',
            loadComponent: () =>
              import('./screens/track-stats-screen/track-stats-screen.component').then(
                (m) => m.TrackStatsScreenComponent,
              ),
          },
          {
            path: 'tracks/:trackId',
            loadComponent: () =>
              import('./screens/track-detail-screen/track-detail-screen.component').then(
                (m) => m.TrackDetailScreenComponent,
              ),
          },
          {
            path: 'vouchers',
            loadComponent: () =>
              import('./elements/vouchers/vouchers-list-wrapper.component').then(
                (m) => m.VouchersListWrapperComponent,
              ),
          },
          {
            path: 'settings',
            loadComponent: () =>
              import('./screens/settings-screen/settings-screen.component').then(
                (m) => m.SettingsScreenComponent,
              ),
            children: [
              {
                path: '',
                pathMatch: 'full',
                redirectTo: 'profile',
              },
              {
                path: 'profile',
                loadComponent: () =>
                  import('./elements/settings/settings-profile-tab.component').then(
                    (m) => m.SettingsProfileTabComponent,
                  ),
              },
              {
                path: 'account',
                loadComponent: () =>
                  import('./elements/settings/settings-account-tab.component').then(
                    (m) => m.SettingsAccountTabComponent,
                  ),
              },
              {
                path: 'privacy',
                loadComponent: () =>
                  import('./elements/settings/settings-privacy-tab.component').then(
                    (m) => m.SettingsPrivacyTabComponent,
                  ),
              },
              {
                path: 'release-notes',
                loadComponent: () =>
                  import('./elements/settings/release-notes.component').then(
                    (m) => m.ReleaseNotesComponent,
                  ),
              },
            ],
          },
        ],
      },
      {
        path: '**',
        redirectTo: '',
      },
    ],
  },
];
